import type { ReactNode } from 'react'
import type { SessionView } from '~/helpers/learner/session-view'
import { useTranslation } from 'react-i18next'
import { CompletionBadge } from '~/components/CompletionBadge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import { useBranding } from '~/helpers/branding/branding-context'
import { DATE_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { formatSeconds } from '~/helpers/learner/duration'
import { completionStatus, finishedAt, progressPct, steps, stepScore, timeSpentSeconds } from '~/helpers/learner/session-view'
import { tourAnchor } from '~/helpers/tour/anchors'
import ProfileSection from './ProfileSection'

const NONE = '—'

/** Score d'une session à un seul module ; un parcours n'a pas de score unique à afficher. */
function sessionScore(session: SessionView): string {
  const all = steps(session)
  const score = all.length === 1 ? stepScore(all[0]!) : null
  if (!score) return NONE
  return score.max != null ? `${score.raw} / ${score.max}` : String(score.raw)
}

type ProfileSessionsProps = {
  sessions: SessionView[]
  /** Action d'une ligne (relevé d'assiduité, #322), pour qui y a droit. */
  renderAction?: (session: SessionView) => ReactNode
}

/** Les sessions de l'apprenant (#242) : état, progression, score, temps passé, date de fin. */
function ProfileSessions({ sessions, renderAction }: ProfileSessionsProps) {
  const { t, i18n } = useTranslation()
  const { timezone } = useBranding()

  return (
    <ProfileSection title={t('profile.sessions.title')} anchor="profile.sessions">
      {sessions.length === 0
        ? <p className="text-sm text-muted-foreground">{t('user.sessions.empty')}</p>
        : (
            <div className="rounded-md border">
              <Table aria-label={t('profile.sessions.title')}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-48 whitespace-normal">{t('sessions.name')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead>{t('user.sessions.progress')}</TableHead>
                    <TableHead>{t('common.score')}</TableHead>
                    <TableHead>{t('profile.sessions.timeSpent')}</TableHead>
                    <TableHead>{t('profile.sessions.finishedAt')}</TableHead>
                    {renderAction && <TableHead {...tourAnchor('profile.attendance')}><span className="sr-only">{t('profile.sessions.actions')}</span></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => {
                    const end = finishedAt(session)
                    return (
                      <TableRow key={session.id}>
                        <TableCell className="min-w-48 whitespace-normal font-medium">{session.name}</TableCell>
                        <TableCell><CompletionBadge status={completionStatus(session)} /></TableCell>
                        <TableCell>
                          {t('profile.sessions.progressValue', {
                            done: session.progress?.completedModules ?? 0,
                            total: session.progress?.totalModules ?? steps(session).length,
                            pct: progressPct(session),
                          })}
                        </TableCell>
                        <TableCell>{sessionScore(session)}</TableCell>
                        <TableCell>{formatSeconds(timeSpentSeconds(session), t) ?? NONE}</TableCell>
                        <TableCell>{end ? formatSessionDate(end, i18n.language, timezone, DATE_WITH_YEAR) : NONE}</TableCell>
                        {renderAction && <TableCell className="text-right">{renderAction(session)}</TableCell>}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
    </ProfileSection>
  )
}

export default ProfileSessions
